import React, { Dispatch, SetStateAction } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useSnackbar from '@hooks/useSnackbar'
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material'

import { Formik, Form, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { putNotificationRecipients as putShoppingcardNotificationRecipients } from '@services/shoppingcardservice'
import { putNotificationRecipients as putContactNotificationRecipients } from '@services/contactservice'
import { putNotificationRecipients as putFeedbackNotificationRecipients } from '@services/feedbackuserservice'
import { putNotificationRecipients as putMinistryNotificationRecipients } from '@services/orderscardservice'
import { CustomDialogProps } from '..'

type EditRecipientFormValuesType = {
  name: string
  email: string
}

export type EditRecipientProps = {
  setOpen: Dispatch<SetStateAction<boolean>>
  onOk: Function | null
  variant: CustomDialogProps['variant']
  recipientid: number
  name: string
  email: string
}

export default function EditRecipientDialog({
  setOpen,
  onOk,
  recipientid,
  variant,
  name,
  email,
}: EditRecipientProps) {
  const intl = useIntl()
  const { toggleSnackbar } = useSnackbar()

  const initialValues: EditRecipientFormValuesType = {
    name: name,
    email: email,
  }

  const validationSchema = Yup.object({
    name: Yup.string().required(
      intl.formatMessage({ id: 'form.validation.required' })
    ),
    email: Yup.string()
      .email()
      .required(intl.formatMessage({ id: 'form.validation.required' })),
  })

  function onSubmitClick(
    values: EditRecipientFormValuesType,
    actions: FormikHelpers<EditRecipientFormValuesType>
  ): void {
    if (variant === 'shoppingcard') {
      putShoppingcardNotificationRecipients(recipientid, values)
        .then(() => {
          toggleSnackbar(
            intl.formatMessage({ id: 'snackbar.success' }),
            'success'
          )
          actions.setSubmitting(false)
          if (onOk) onOk()
          setOpen(false)
        })
        .catch((e) => {
          console.log(e)
          toggleSnackbar(intl.formatMessage({ id: 'snackbar.error' }), 'error')
        })
    } else if (variant === 'feedback') {
      putFeedbackNotificationRecipients(recipientid, values)
        .then(() => {
          toggleSnackbar(
            intl.formatMessage({ id: 'snackbar.success' }),
            'success'
          )
          actions.setSubmitting(false)
          if (onOk) onOk()
          setOpen(false)
        })
        .catch((e) => {
          console.log(e)
          toggleSnackbar(intl.formatMessage({ id: 'snackbar.error' }), 'error')
        })
    } else if (variant === 'ministry') {
      putMinistryNotificationRecipients(recipientid, values)
        .then(() => {
          toggleSnackbar(
            intl.formatMessage({ id: 'snackbar.success' }),
            'success'
          )
          actions.setSubmitting(false)
          if (onOk) onOk()
          setOpen(false)
        })
        .catch((e) => {
          console.log(e)
          toggleSnackbar(intl.formatMessage({ id: 'snackbar.error' }), 'error')
        })
    } else {
      putContactNotificationRecipients(recipientid, values)
        .then(() => {
          toggleSnackbar(
            intl.formatMessage({ id: 'snackbar.success' }),
            'success'
          )
          actions.setSubmitting(false)
          if (onOk) onOk()
          setOpen(false)
        })
        .catch((e) => {
          console.log(e)
          toggleSnackbar(intl.formatMessage({ id: 'snackbar.error' }), 'error')
        })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitClick}
    >
      {({
        isSubmitting,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
      }) => (
        <Form>
          <DialogTitle>
            <FormattedMessage id="dialog.editrecipient.title" />
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <FormattedMessage id="dialog.editrecipient.copy" />
            </DialogContentText>
            <Grid container spacing={4} direction="column">
              <Grid item>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  fullWidth
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={!!touched.name && !!errors.name && errors.name}
                />
              </Grid>
              <Grid item>
                <TextField
                  id="email"
                  name="email"
                  label="E-Mail Adresse"
                  fullWidth
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={!!touched.email && !!errors.email && errors.email}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={isSubmitting}>
              <FormattedMessage id="button.abort" />
            </Button>
            <Button type="submit" color="primary" disabled={isSubmitting}>
              <FormattedMessage id="button.ok" />
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  )
}
